import React, { FC } from 'react';
import { AccountingType } from 'v2-types/order';

import usePolicyRefactor from './policy-info-refactor-hooks';
import { updateType, PolicyContext } from './contexts/policyInfoContext';
import { FormPolicy } from './services/types';
import {
  Paper,
  Box,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FormProvider } from 'react-hook-form';

import Balance from './components/balance';
import TableEndorsements from './components/endorsements-list';
import { Header } from './components/header';
import PolicyTypeForm from './components/policy-type-form';
import { SkeletonPolicy } from './components/skeleton-policy';
import AutoSave from './services/autosave';
import { PolicyTabs } from './components/Tabs';
import { GeneralChargesProvider } from 'contexts/GeneralChargesContext';
import { GeneralTableCharges } from '../charges/components/GeneralChargesTable';
import { CardChargesList } from './components/charges-cards/CardChargesList';
import { InsuranceCoverageText } from './components/InsuranceCoverageText';
import { NoDataFoundAlert } from './components/NoDataFoundAlert';

const useStyles = makeStyles((theme: any) => ({
  rootContainer: { padding: theme.spacing(3) },
  mainContainer: { padding: theme.spacing(3) },
  alertContainer: { marginBottom: theme.spacing(4) },
  alertLink: {
    fontWeight: 'bold',
    textDecoration: 'none',
    borderBottom: '1px dotted',
    color: theme.palette.secondary.main,
  },
  alertLinkIcon: { marginLeft: theme.spacing(0.7) },
  alertItalic: { fontStyle: 'italic' },
  lendersContainer: { marginTop: theme.spacing(2) },
  formPolicyTypeContainer: { marginTop: theme.spacing(2) },
  tableEndorsementsContainer: { marginTop: theme.spacing(4) },
  tableOwnersPolicyTaxesAndFeesContainer: { marginTop: theme.spacing(2) },
  formPremiumsContainer: { marginTop: theme.spacing(5) },
  calculatorContainer: { paddingLeft: theme.spacing(2) },
  drawer: {
    width: 400,
    flexShrink: 0,
  },
  drawerPaper: { width: 400 },
  container: { width: '100%', padding: theme.spacing(2) },
  contentContainer: {
    marginTop: '1rem',
  },
  mainRow: {
    display: 'flex',
    gap: '1rem',
    [theme.breakpoints.down('lg')]: {
      flexDirection: 'column',
    },
  },
  primaryRow: {
    display: 'flex',
    gap: '1rem',
    flex: 1,
    minWidth: 0,
    [theme.breakpoints.down('lg')]: {
      order: 2,
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  balanceWrapper: {
    [theme.breakpoints.down('lg')]: {
      order: 1,
    },
  },
  formPaper: {
    [theme.breakpoints.down('lg')]: {
      flex: 1,
      minWidth: 0,
    },
  },
  endorsementsPaper: {
    flex: '1',
    [theme.breakpoints.down('lg')]: {
      minWidth: 0,
    },
  },
}));

type Props = {
  orderType: string
}

export const PolicyRefactor: FC<Props> = ({ orderType }) => {
  const {
    loadingData,
    accounting,
    methods,
    options,
    setAccounting,
    info,
    setInfo,
    updateOrderInfo,
    id,
    typeTransaction,
    isPolicyTabsLoading,
    tabs,
    selectedTab,
    showNoDataFoundMessage,
    insuranceInfo,
    handleSelectTab,
    selectedTabIdRef,
    endorsements,
    setEndorsements
  } = usePolicyRefactor();

  const classes = useStyles();
  const theme = useTheme();
  const isChargesCardLayout = useMediaQuery(theme.breakpoints.down('lg'));

  const handleSetAccounting = (accountingData: AccountingType[]) => {
    setAccounting(accountingData);
  };
  const handleSave = (formValues: FormPolicy) => {
    if (!selectedTabIdRef.current) return;

    const data = formValues[selectedTabIdRef.current];
    delete data.endorsements;

    updateOrderInfo({
      id,
      kind:
        selectedTabIdRef.current === "owners_policy_tab" ? 'owners' : 'lenders',
      data
    });
  };

  const handleUpdateInfo = (infovalues: updateType) => {
    setInfo(infovalues);
  };

  return (
    <PolicyContext.Provider
      value={{
        accounting,
        setAccounting: handleSetAccounting,
        options,
        loading: false,
        latestUpdate: info,
        setLatestUpdate: handleUpdateInfo,
        isPolicyTabsLoading,
        tabs,
        selectedTab,
        handleSelectTab,
        insuranceInfo,
        selectedTabIdRef,
        endorsements,
        setEndorsements
      }}
    >
      <Box className={classes.container}>

        {loadingData ? (
          <SkeletonPolicy />
        ) : (
          <>
            <Header />
            {showNoDataFoundMessage && (<NoDataFoundAlert />)}

            {!showNoDataFoundMessage && (
              <Box className={classes.contentContainer}>
                <Box className={classes.mainRow}>
                  <Box className={classes.primaryRow}>
                    <Paper className={classes.formPaper}>
                      <FormProvider {...methods}>
                        <Box style={{ padding: "1rem" }}>
                          <PolicyTabs />
                          <Box style={{ marginTop: "1.5rem" }}>
                            {insuranceInfo && (<InsuranceCoverageText />)}
                            <Box style={{ display: "flex", gap: "1.5rem", marginTop: "1.5rem", }}>
                              <PolicyTypeForm />
                            </Box>
                          </Box>
                        </Box>
                        <AutoSave handleSave={handleSave} />
                      </FormProvider>
                    </Paper>

                    <Paper className={classes.endorsementsPaper}>
                      <TableEndorsements />
                    </Paper>
                  </Box>
                  <Box className={classes.balanceWrapper}>
                    <Balance typeTransaction={typeTransaction} />
                  </Box>
                </Box>

                {selectedTab && (
                  <Paper style={{ marginTop: "1rem" }}>
                    <GeneralChargesProvider
                      orderType={orderType}
                      hideAddEntry
                      hideEndorsementCode={false}
                      accountingFilter={selectedTab.id === "owners_policy_tab" ? `/^owners_(policy|tx_guaranty_fee|\\d+)$/` : `/^lenders_(policy|tx_guaranty_fee|\\d+)$/`}
                    >
                      {isChargesCardLayout ? (
                        <CardChargesList orderType={orderType} />
                      ) : (
                        <GeneralTableCharges orderType={orderType} />
                      )}
                    </GeneralChargesProvider>
                  </Paper>
                )}
              </Box>
            )}
          </>
        )}
      </Box>
    </PolicyContext.Provider>
  );
};
